import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Target, ArrowUpRight, TrendingUp, 
  ChevronRight, Award, UserCheck, ShieldAlert, Sparkles, BarChart3, Crown,
  Wallet, Calendar, Clock, CheckCircle2, ShieldCheck, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { managerAPI } from '../utils/api';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await managerAPI.getDashboard();
        if (res.data.success) {
          setData(res.data);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const bds = data?.bds || [];
  const topAgencies = data?.top_agencies || [];
  const recentHosts = data?.recent_hosts || [];
  const chartData = data?.chart_data || [];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
        {/* Welcome Banner */}
        <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-r from-indigo-900/60 via-purple-900/40 to-slate-900/70 border border-indigo-500/20 p-8 md:p-10 shadow-2xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-3">
                <Crown size={14} className="text-indigo-400" />
                <span>Executive Management Center</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-black text-white tracking-tight">
                Manager Portfolio Overview
              </h1>
              <p className="text-slate-400 text-sm mt-1 max-w-xl">
                Real-time performance tracking across all assigned Business Development executives, agencies, and hosts.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/manager/bds')}
                className="px-5 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm shadow-lg shadow-indigo-600/30 hover:opacity-95 transition-all flex items-center gap-2 cursor-pointer"
              >
                <UserCheck size={18} />
                <span>Manage BD Team</span>
              </button>
            </div>
          </div>
        </div>

        {/* Manager Commission & Valid Days Highlight Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card A: Pending Commission Diamonds */}
          <div className="bg-gradient-to-br from-amber-950/40 via-[#0b0f19] to-[#0b0f19] border border-amber-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center text-amber-400">
                <Sparkles size={24} />
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                stats.is_commission_unlocked 
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}>
                {stats.is_commission_unlocked ? 'Unlocked 🎉' : '10-Day Hold'}
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Pending Commission</h3>
            <p className="text-3xl font-black text-amber-400 mt-1 font-mono">
              {(stats.pending_diamonds || 0).toLocaleString()} <span className="text-lg">💎</span>
            </p>
            <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1.5">
              {stats.is_commission_unlocked ? (
                <span className="text-emerald-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 size={13} /> 10 Days Target Met — Released!
                </span>
              ) : (
                <span className="text-amber-300 font-semibold flex items-center gap-1">
                  <AlertCircle size={13} /> {stats.days_remaining ?? Math.max(0, 10 - (stats.valid_days || 0))} more valid days needed to unlock
                </span>
              )}
            </p>
          </div>

          {/* Card B: Manager Valid Days Progress */}
          <div className="bg-gradient-to-br from-indigo-950/40 via-[#0b0f19] to-[#0b0f19] border border-indigo-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center text-indigo-400">
                <Calendar size={24} />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-indigo-500/15 text-indigo-300 font-bold text-xs border border-indigo-500/30">
                {stats.valid_days || 0} / 10 Days
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Manager Active Days</h3>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="text-3xl font-black text-white">{stats.valid_days || 0}</p>
              <span className="text-sm text-slate-400 font-bold">/ 10 Valid Days</span>
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-white/5 rounded-full h-2 mt-3 overflow-hidden border border-white/10">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ${
                  stats.is_commission_unlocked 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-400' 
                    : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                }`}
                style={{ width: `${Math.min(100, Math.round(((stats.valid_days || 0) / 10) * 100))}%` }}
              ></div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
              Daily seat/mic session timer (2+ hrs daily)
            </p>
          </div>

          {/* Card C: Manager Available Wallet */}
          <div className="bg-gradient-to-br from-emerald-950/40 via-[#0b0f19] to-[#0b0f19] border border-emerald-500/25 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-400">
                <Wallet size={24} />
              </div>
              <button 
                onClick={() => navigate('/manager/performance')}
                className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-bold transition flex items-center gap-1 cursor-pointer"
              >
                <span>Ledger</span>
                <ChevronRight size={12} />
              </button>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Wallet Balance</h3>
            <p className="text-3xl font-black text-emerald-400 mt-1 font-mono">
              {(stats.diamond_balance || 0).toLocaleString()} <span className="text-lg">💎</span>
            </p>
            <p className="text-[11px] text-slate-400 mt-2">
              Credited: <span className="text-white font-semibold">{(stats.credited_diamonds || 0).toLocaleString()} 💎</span> released this month
            </p>
          </div>
        </div>

        {/* Top 4 KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card 1: My BDs */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                <UserCheck size={24} />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-indigo-500/10 text-indigo-400 font-bold text-xs">
                {stats.active_bds || 0} Active
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">BD Team Members</h3>
            <p className="text-3xl font-black text-white mt-1">{stats.total_bds || 0}</p>
            <p className="text-[11px] text-slate-500 mt-2">Executives under your direction</p>
          </div>

          {/* Card 2: Managed Agencies */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <Building2 size={24} />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-400 font-bold text-xs">
                {stats.active_agencies || 0} Active
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Managed Agencies</h3>
            <p className="text-3xl font-black text-white mt-1">{stats.total_agencies || 0}</p>
            <p className="text-[11px] text-slate-500 mt-2">Across all your BD partners</p>
          </div>

          {/* Card 3: Network Hosts */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                <Users size={24} />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 font-bold text-xs">
                Active Network
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Hosts</h3>
            <p className="text-3xl font-black text-white mt-1">{stats.total_hosts || 0}</p>
            <p className="text-[11px] text-slate-500 mt-2">Signed hosts under agencies</p>
          </div>

          {/* Card 4: Month Coins Generated (Target Progress Removed) */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-3xl p-6 relative overflow-hidden backdrop-blur-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <TrendingUp size={24} />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 font-bold text-xs">
                Active Month
              </span>
            </div>
            <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider">Month Coins Generated</h3>
            <p className="text-2xl font-black text-amber-400 mt-1">
              {(stats.achieved_amount || 0).toLocaleString()} <span className="text-xs text-slate-400 font-normal">coins</span>
            </p>
            <p className="text-[11px] text-slate-500 mt-2">Combined coins across entire network</p>
          </div>
        </div>

        {/* BD Team Section & 7-Day Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* BD Team Performance Table (2 Columns) */}
          <div className="lg:col-span-2 bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserCheck size={20} className="text-indigo-400" />
                  <span>BD Team Performance</span>
                </h3>
                <p className="text-xs text-slate-400">Portfolio breakdown per Business Development executive</p>
              </div>
              <button 
                onClick={() => navigate('/manager/bds')}
                className="text-xs font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1 cursor-pointer"
              >
                <span>View All BDs</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {bds.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                No BDs assigned under your management yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="pb-3">BD Executive</th>
                      <th className="pb-3 text-center">Agencies</th>
                      <th className="pb-3 text-center">Hosts</th>
                      <th className="pb-3 text-right">Target Coins</th>
                      <th className="pb-3 text-right">Commission</th>
                      <th className="pb-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-sm font-medium">
                    {bds.map((bd) => (
                      <tr key={bd.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold text-xs">
                              {bd.first_name?.charAt(0) || 'B'}
                            </div>
                            <div>
                              <p className="font-bold text-white text-sm">{bd.first_name} {bd.last_name}</p>
                              <p className="text-[11px] text-indigo-400 font-mono font-semibold">{bd.user_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-slate-300 font-bold text-xs">
                            {bd.agencies_count || 0}
                          </span>
                        </td>
                        <td className="py-4 text-center">
                          <span className="px-2.5 py-1 rounded-lg bg-cyan-500/10 text-cyan-400 font-bold text-xs">
                            {bd.hosts_count || 0}
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <span className="font-bold text-amber-400 font-mono">
                            {(bd.monthly_target_coins || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <span className="font-bold text-emerald-400 font-mono text-xs">
                            {(bd.total_manager_commission_diamonds || 0).toLocaleString()} 💎
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <button
                            onClick={() => navigate(`/manager/agencies?bd_id=${bd.id}`)}
                            className="p-2 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 hover:text-white transition-all cursor-pointer"
                            title="View BD Agencies"
                          >
                            <ArrowUpRight size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 7-Day Trend Card (1 Column) */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <BarChart3 size={20} className="text-purple-400" />
                    <span>7-Day Network Activity</span>
                  </h3>
                  <p className="text-xs text-slate-400">Host activity cadence</p>
                </div>
              </div>

              {/* Bar Chart Representation */}
              <div className="h-44 flex items-end justify-between gap-3 pt-4 px-2">
                {chartData.map((item, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                    <div 
                      className="w-full rounded-xl bg-gradient-to-t from-indigo-600 via-purple-600 to-violet-500 hover:opacity-90 transition-all cursor-pointer relative group"
                      style={{ 
                        height: `${Math.max(15, Math.min(100, (item.coins / (Math.max(...chartData.map(c => c.coins)) || 1)) * 100))}%` 
                      }}
                    >
                      <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/90 text-[10px] text-white rounded font-mono pointer-events-none whitespace-nowrap transition-opacity">
                        {item.coins.toLocaleString()}
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400 font-bold">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400">Total Network Output</p>
                <p className="text-lg font-bold text-white font-mono">{(stats.achieved_amount || 0).toLocaleString()} Coins</p>
              </div>
              <button 
                onClick={() => navigate('/manager/performance')}
                className="px-4 py-2 rounded-xl bg-indigo-500/10 text-indigo-400 text-xs font-bold hover:bg-indigo-500/20 transition-all cursor-pointer"
              >
                Analytics
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Row: Top Agencies & Recent Hosts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Agencies */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Building2 size={20} className="text-purple-400" />
                  <span>Top Agencies</span>
                </h3>
                <p className="text-xs text-slate-400">Leading agencies across all your BDs</p>
              </div>
              <button 
                onClick={() => navigate('/manager/agencies')}
                className="text-xs font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1 cursor-pointer"
              >
                <span>All Agencies</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {topAgencies.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                No agencies registered yet.
              </div>
            ) : (
              <div className="space-y-3">
                {topAgencies.map((agency) => (
                  <div key={agency.id} className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center justify-between hover:bg-white/[0.04] transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                        {agency.agency_name?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-sm">{agency.agency_name}</h4>
                        <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                          <span>Code: <b className="text-slate-300">{agency.agency_code}</b></span>
                          <span>•</span>
                          <span className="text-indigo-400">BD: {agency.bd_name || agency.bd_code || 'Assigned'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-amber-400 font-mono">
                        {(agency.monthly_target_coins || 0).toLocaleString()}
                      </p>
                      <p className="text-xs font-bold text-emerald-400 font-mono">
                        {(agency.total_manager_commission_diamonds || 0).toLocaleString()} 💎
                      </p>
                      <p className="text-[11px] text-slate-400">{agency.hosts_count || 0} Hosts</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Hosts */}
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] p-6 md:p-8 backdrop-blur-xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users size={20} className="text-cyan-400" />
                  <span>Recent Network Hosts</span>
                </h3>
                <p className="text-xs text-slate-400">Latest active onboarded talents</p>
              </div>
              <button 
                onClick={() => navigate('/manager/hosts')}
                className="text-xs font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1 cursor-pointer"
              >
                <span>All Hosts</span>
                <ChevronRight size={14} />
              </button>
            </div>

            {recentHosts.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                No hosts found in the network.
              </div>
            ) : (
              <div className="space-y-3">
                {recentHosts.slice(0, 5).map((host) => (
                  <div key={host.id} className="p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center justify-between hover:bg-white/[0.04] transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xs">
                        {host.avatar_url ? (
                          <img src={host.avatar_url} alt="" className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          (host.first_name || host.user_name || 'H').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-white text-sm">
                          {host.first_name ? `${host.first_name} ${host.last_name || ''}`.trim() : (host.user_name || 'Host')}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span className="font-mono text-cyan-400">{host.xoxo_id || host.id?.substring(0, 8)}</span>
                          <span>•</span>
                          <span>Agency: {host.agency_name || host.agency_code}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {/* Host Target Level Starts at 0 */}
                      <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 font-bold text-xs">
                        Lv.{Number(host.current_level || 0)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default ManagerDashboard;
